import React, { Fragment, useState } from "react";
import "../documentation.css";
import { formatTimestamp } from "../helpers";
import { DoneIcon, TodoIcon } from "../../assets/Icons";
import quizzesObj from "./quizzes";
import readingsObj from "./readings";
import formsObj from "./forms";
import CandImage from "../Components/CandImage";
import Initials from "../Components/Initials";

const DavinFoodsDocuments = ({
  candidates,
  quizzes,
  images,
  passImage,
  handleOverlay,
  getCompletedFormPdf,
  downloadRef
}) => {
  const [selected, setSelected] = useState(null);
  const [lineSelected, setLineSelected] = useState();
  const [highlightBtn, setHighlightBtn] = useState("");

  const candidateQuizzes = quizzes || {};

  const candidateReadings = candidates.reduce((acc, candidate) => {
    const {
      candidateRef,
      documents: { readings }
    } = candidate;
    acc[candidateRef] = readings;
    return acc;
  }, {});

  const candidateForms = candidates.reduce((acc, candidate) => {
    const { candidateRef, nonReadingListDocs } = candidate;
    acc[candidateRef] = nonReadingListDocs;
    return acc;
  }, {});

  const documentsObj = {
    quizzes: {
      serverData: candidateQuizzes,
      localData: quizzesObj.quizzes
    },
    readings: {
      serverData: candidateReadings,
      localData: readingsObj.readings
    },
    forms: {
      serverData: candidateForms,
      localData: formsObj.forms
    }
  };

  const handleDownloadForm = (candidate, documentTitle, documentID) => {
    const candidateName = `${candidate.firstName} ${candidate.lastName}`;
    return getCompletedFormPdf(
      candidate.candidateRef,
      candidateName,
      documentID,
      documentTitle
    );
  };

  const ViewButton = ({ id, fn, num }) => (
    <div className="tw-flex tw-flex-col tw-items-center tw-content-center tw-flex-1 tw-py-10">
      <button
        className={`${id === highlightBtn &&
          "tw-border-blue-400 tw-text-white tw-bg-blue-400 tw-border-2"} tw-bg-blue-200' tw-bg-blue-100 tw-p-2 lg:tw-w-1/2 tw-w-full tw-rounded-lg ${id !==
          highlightBtn &&
          "hover:tw-bg-blue-200 hover:tw-border-blue-500"} tw-border-blue-300 tw-border-2 tw-cursor-pointer`}
        onClick={fn}
      >
        <span className="md:tw-inline tw-hidden">View</span>
        <span className="md:tw-hidden tw-inline">{num}</span>
      </button>
    </div>
  );

  const getDocumentsCount = (line, candidateRef) => {
    const localDocs = documentsObj[line].localData;
    const serverDocs = documentsObj[line].serverData;
    const values = Object.values(localDocs);
    let readCount = 0;
    if (serverDocs[candidateRef]) {
      readCount = values.filter(
        quiz => serverDocs[candidateRef][quiz.id] !== undefined
      ).length;
    }
    return { readCount };
  };

  const DownloadButton = ({ candidate, documentTitle, documentID }) => (
    <button
      onClick={() => handleDownloadForm(candidate, documentTitle, documentID)}
      style={{
        fontSize: "14px",
        backgroundColor: "white",
        padding: "8px",
        borderRadius: "5px",
        border: "1px solid #2196f3"
      }}
    >
      Download
    </button>
  );

  const showReadButton = (line, candidate, id, title) =>
    line === "readings" ? (
      <DoneIcon />
    ) : (
      <DownloadButton
        candidate={candidate}
        documentID={id}
        documentTitle={title}
      />
    );
  const showReadButtonTooltipMessage = (readTimestamp, line) =>
    line === "readings" ? (
      <div className="tooltiptext">
        {`Read ${formatTimestamp(readTimestamp)}`}
      </div>
    ) : null;

  const FileContainer = ({ candidate, title, id, readTimestamp, line }) => (
    <div className="tw-bg-gray-50 tw-flex tw-flex-col tw-content-center tw-text-center tw-p-2 tw-m-4 tw-h-40 tw-min-h-fit tw-w-52 tw-border-2 tw-items-center">
      <p className="tw-mb-1">{title}</p>
      <div
        className="tw-w-full tw-h-full tw-flex"
        style={{ alignItems: "end" }}
      >
        <div className="tw-flex-1 tw-flex-col tw-py-2">
          {readTimestamp ? (
            <div className="tooltip">
              {showReadButton(line, candidate, id, title)}
              {showReadButtonTooltipMessage(readTimestamp, line)}
            </div>
          ) : (
            <div className="tooltip">
              <TodoIcon />
            </div>
          )}
          <p className="tw-pt-2">{line === "readings" && "Read"}</p>
        </div>
      </div>
    </div>
  );

  const populateDropRow = (line, c) => {
    const localDocs = documentsObj[line].localData;
    const serverDocs = documentsObj[line].serverData;
    return localDocs.map((row, i) => {
      const { id, title } = row;
      const timestamp =
        serverDocs[c.candidateRef] && serverDocs[c.candidateRef][id];
      if (timestamp) {
        return (
          <FileContainer
            candidate={c}
            id={id}
            line={line}
            key={i}
            title={title}
            readTimestamp={timestamp}
          />
        );
      }

      return <FileContainer key={i} title={title} line={line} />;
    });
  };

  const Header = ({ title, line, countObj: { readCount } }) => {
    const documentsLength = documentsObj[line].localData.length;
    return (
      <div className="tw-w-full tw-flex md:tw-flex-row tw-flex-col md:tw-justify-between tw-items-start md:tw-p-5 tw-p-3">
        <p className="md:tw-pl-2 tw-pl-0 tw-text-md tw-pb-2">{title}</p>
        <div className="tw-w-auto tw-flex md:tw-justify-between tw-mr-2">
          <span>
            {readCount} / {documentsLength}{" "}
            {line === "quizzes" ? "quizzes completed" : "booklets read"}
          </span>
        </div>
      </div>
    );
  };

  const setState = (i, line) => {
    if (selected === i && line === lineSelected) {
      setSelected(null);
      setLineSelected(null);
    } else {
      setSelected(i);
      setLineSelected(line);
    }
  };

  const setHighligtning = key =>
    highlightBtn === key ? setHighlightBtn("") : setHighlightBtn(key);

  const getHighestPriorityWorkHistory = typeCodes => {
    const priorityList = ["YCV1", "WPC1", "YWOR"];
    return priorityList.find(type => typeCodes.includes(type));
  };

  const handleCvWorkHistory = c => {
    const cvWorkHistory = candidateForms[c.candidateRef];
    const highestPriorityWorkHistory = getHighestPriorityWorkHistory(
      Object.keys(cvWorkHistory)
    );
    if (highestPriorityWorkHistory) {
      return (
        <div className="tooltip">
          <DownloadButton
            candidate={c}
            documentID={highestPriorityWorkHistory}
            documentTitle={cvWorkHistory[highestPriorityWorkHistory]}
          />
        </div>
      );
    }
    return (
      <div className="tooltip">
        <TodoIcon />
      </div>
    );
  };

  return (
    <div>
      <div className="tw-rounded-md tw-overflow-x-auto ">
        <div className="tw-align-middle tw-rounded-md tw-inline-block tw-min-w-full">
          <div className="tw-shadow tw-rounded-md tw-overflow-hidden tw-border-b tw-border-gray-200 ">
            <table className="tw-overflow-x tw-border-collapse tw-min-w-full tw-border-2 tw-border-gray-500 tw-rounded-md tw-mb-8 tw-divide-y tw-divide-gray-200">
              <thead className="tw-border-collapse tw-bg-gray-50 tw-rounded-md tw-border-0 tw-border-gray-500 ">
                <tr>
                  <th
                    scope="tw-col"
                    style={{ width: "25%" }}
                    className="tw-py-4 tw-px-2 tw-border-2 tw-rounded-md tw-border-gray-500 tw-text-left tw-text-md tw-font-medium tw-text-gray-500 tw-text-center"
                  >
                    Worker
                  </th>
                  <th
                    key="readings"
                    scope="col"
                    className="tw-w-3/12 tw-border-2 tw-rounded-md tw-border-gray-500 tw-text-md tw-font-medium tw-text-gray-500 tw-text-center"
                  >
                    <div className="tw-flex tw-flex-col tw-min-h-full tw-border-black">
                      <span className="tw-p-5">Induction Booklets</span>
                    </div>
                  </th>

                  <th
                    key="quizzes"
                    scope="col"
                    className="tw-w-3/12 tw-border-2 tw-rounded-md tw-border-gray-500 tw-text-md tw-font-medium tw-text-gray-500 tw-text-center"
                  >
                    <div className="tw-flex tw-flex-col tw-min-h-full tw-border-black">
                      <span className="tw-p-5">Quizzes</span>
                    </div>
                  </th>
                  {formsObj.forms.map(doc => (
                    <th
                      key={doc.id}
                      scope="col"
                      className="tw-py-4 tw-px-2 tw-border-2 tw-rounded-md tw-border-gray-500 tw-text-left tw-text-md tw-font-medium tw-text-gray-500 tw-text-center"
                    >
                      {doc.title}
                    </th>
                  ))}
                  <th
                    key="cvWorkHistory"
                    scope="col"
                    className="tw-py-4 tw-px-2 tw-border-2 tw-rounded-md tw-border-gray-500 tw-text-left tw-text-md tw-font-medium tw-text-gray-500 tw-text-center"
                  >
                    CV / Work History
                  </th>
                </tr>
              </thead>
              <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
                {candidates.map((c, i) => (
                  <Fragment key={i}>
                    <tr className="reading-list__rows">
                      <td className="tw-text-sm">
                        <div className="tw-flex lg:tw-flex-row tw-flex-col tw-w-full lg:tw-items-center md:tw-text-left tw-text-center">
                          {images && images[c.candidateRef] ? (
                            <CandImage
                              handleOverlay={handleOverlay}
                              passImage={passImage}
                              image={images[c.candidateRef]}
                              firstName={c.firstName}
                            />
                          ) : (
                            <Initials
                              firstName={c.firstName}
                              lastName={c.lastName}
                            />
                          )}
                          <p className="tw-font-bold tw-font-lg lg:tw-pl-0 tw-pl-1">
                            {c.firstName} {c.lastName}{" "}
                          </p>
                        </div>
                      </td>
                      <td>
                        <div
                          className={`tw-flex tw-min-h-full tw-w-full tw-min-w-fit`}
                        >
                          <ViewButton
                            id={`${i}${c.lastName}reading`}
                            fn={() => {
                              setState(i, "readings");
                              setHighligtning(`${i}${c.lastName}`);
                            }}
                            num="reading"
                          />
                        </div>
                      </td>
                      <td>
                        <div
                          className={`tw-flex tw-min-h-full tw-w-full tw-min-w-fit`}
                        >
                          <ViewButton
                            id={`${i}${c.lastName}form`}
                            fn={() => {
                              setState(i, "quizzes");
                              setHighligtning(`${i}${c.lastName}`);
                            }}
                            num="form"
                          />
                        </div>
                      </td>
                      {formsObj.forms.map(form => (
                        <td className="tw-p-2" key={`th${form.id}`}>
                          <div className="tw-flex-1 tw-flex-col tw-py-2">
                            {candidateForms[c.candidateRef][form.id] ? (
                              <div className="tooltip">
                                <DownloadButton
                                  candidate={c}
                                  documentID={form.id}
                                  documentTitle={form.title}
                                />
                              </div>
                            ) : (
                              <div className="tooltip">
                                <TodoIcon />
                              </div>
                            )}
                          </div>
                        </td>
                      ))}
                      <td className="tw-p-2">
                        <div className="tw-flex-1 tw-flex-col tw-py-2">
                          {handleCvWorkHistory(c)}
                        </div>
                      </td>
                    </tr>
                    {selected === i ? (
                      <tr>
                        <td colSpan="6">
                          <Header
                            line={lineSelected}
                            title={`${c.firstName} ${c.lastName}`}
                            countObj={getDocumentsCount(
                              lineSelected,
                              c.candidateRef
                            )}
                          />
                          <div className="tw-flex tw-min-w-full tw-overflow-y-auto tw-flex-wrap tw-pb-4 tw-px-0 tw-justify-center tw-items-center">
                            {populateDropRow(lineSelected, c)}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
            <a className="d-none" ref={downloadRef}></a>
          </div>
        </div>
      </div>
      <a className="d-none" ref={downloadRef}></a>
    </div>
  );
};
export default DavinFoodsDocuments;
